// BaseCore — the system-agnostic engine-swap surface shared by BOTH
// consumer tiers.
//
// The browser tier's `EmulatorCore` (./emulator-core.ts) and the
// headless tier's `HeadlessCore` (./headless-core.ts) are each a
// discriminated union of per-system extensions of this one interface.
// Anything that can be described without reference to a specific
// console's PPU/OAM/palette shape lives here, exactly once — the two
// tiers used to maintain near-identical copies of this block by
// copy-paste, and they drifted.
//
// Notes on the contract:
//
// - `framebuffer` is owned by the core and refreshed in-place each
//   `frame()`. Consumers snapshot/composite after frame(); cores may
//   reuse the buffer freely between frames. Format: Uint32Array,
//   0x00BBGGRR per pixel (low byte = R, high byte unused / alpha).
//   Dimensions are system-specific (NES: 256x240; GB: 160x144) — see
//   screenWidth/screenHeight. Non-native cores convert to this layout
//   once in their adapter.
//
// - `drainAudio()` returns interleaved-stereo Float32 samples since
//   the last drain, at the sample rate the core was constructed with.
//   Consumers drain once per frame. Cores that disable sound
//   (`emulateSound: false`) return `null`.
//
// - `onOamDmaWrite(handler)` registers a callback that fires after an
//   OAM-DMA completes (NES: $4014; GB: $FF46 — same idea on every
//   system that has a DMA register). Hosts use this to drive
//   `event_client_frame` at the moment the game commits sprites for
//   the current frame. Each call replaces the previous handler. A
//   core must call the handler synchronously from inside its CPU
//   step, and the registration must survive loadROM (the wrapper /
//   hook is reinstalled by the core itself).
//
// - `readCpuByte` / `writeCpuByte` access the CPU bus address space
//   (16-bit on both 6502 and SM83). Reads should be side-effect-free
//   where the core supports it. Writes are a full-bus passthrough: a
//   write does exactly what the CPU would do storing to that address,
//   with all hardware side effects (I/O registers, PPU/APU ports, and
//   the mapper/MBC bank-switch window). This is deliberate — Munos
//   `write_u8` is the live machine. Editing the cartridge image itself
//   is a separate operation (`writeRomByte`, physical coordinate).
// Thrown by a core's loadState() when the envelope's `core` tag does
// not match the core doing the loading (or the value isn't an
// envelope at all). Savestates are core-private; feeding one core's
// state to another would silently corrupt machine state, so the
// refusal is loud and typed.
export class SaveStateMismatchError extends Error {
    expected;
    actual;
    constructor(expected, actual) {
        super(`savestate core mismatch: this core is '${expected}' but the state was ` +
            `produced by '${actual}'. Savestates are core-private and cannot cross cores.`);
        this.name = "SaveStateMismatchError";
        this.expected = expected;
        this.actual = actual;
    }
}
function isCoreSaveState(blob) {
    return blob !== null && typeof blob === "object"
        && typeof blob.core === "string"
        && "data" in blob;
}
// Normalize a savestate blob of unknown provenance into the envelope.
//
// Legacy compatibility: `.munos-tas` recordings (and browser
// savestate slots) written before the envelope existed carry the raw
// core-private blob with no tag. Every untagged blob on disk is a
// NesJs state — NES was the only core when they were written — with
// one exception: the GB adapter self-tagged its blobs with
// `__format: "gb-roblouie/v1"` from day one, so that marker routes to
// the GB core. Already-enveloped states pass through unchanged, so
// applying this at every load boundary is idempotent and cheap.
export function coerceLegacySaveState(blob) {
    if (isCoreSaveState(blob))
        return blob;
    if (blob !== null && typeof blob === "object") {
        const format = blob.__format;
        if (typeof format === "string" && format.startsWith("gb-roblouie/")) {
            return { core: "gb-roblouie", data: blob };
        }
    }
    return { core: "nesjs", data: blob };
}
// Validate an envelope against the loading core's id and unwrap it.
// Adapters call this at the top of loadState(); the throw path is the
// "typed error" the contract promises for unsupported savestates.
export function assertSaveStateCore(state, expected) {
    if (!isCoreSaveState(state)) {
        throw new SaveStateMismatchError(expected, "<untagged blob>");
    }
    if (state.core !== expected) {
        throw new SaveStateMismatchError(expected, state.core);
    }
    return state.data;
}
// CPU register selector. The numeric values are an ABI shared with the
// Munos REG_* constants (packages/munos/src/codegen/reg-constants.ts) —
// keep the two in sync. PC is 16-bit; the rest are 8-bit.
export const CpuReg = {
    A: 0, X: 1, Y: 2, SP: 3, P: 4, PC: 5,
};
// Feature-detect the exec-hook capability on a core. Narrows the type so
// callers can use the ExecHookCapable surface without an unchecked cast.
export function supportsExecHooks(core) {
    return typeof core.onExecHook === "function";
}
// Feature-detect the PPU-access capability. Narrows the type so callers can use
// the surface without an unchecked cast.
export function supportsPpuAccess(core) {
    return typeof core.writePpuByte === "function";
}
// Feature-detect the battery capability. Narrows the type so callers can use the
// surface without an unchecked cast. A `true` result does not imply the loaded
// cartridge HAS a battery — that is getBattery() returning non-null.
export function supportsBattery(core) {
    return typeof core.getBattery === "function";
}
// Feature-detect the audio-synth capability on a core. Narrows the type
// so callers can drive a synth instance without an unchecked cast.
export function supportsAudioSynth(core) {
    return typeof core.writeAudioRegister === "function";
}
export function supportsAudioCapture(core) {
    return typeof core.onAudioRegisterWrite === "function";
}
//# sourceMappingURL=base-core.js.map